import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
} from '@angular/core';
import type { ContextFamily, ContextTone } from '@quipex/shared/utils';
import { Skeleton } from './skeleton';

export type QpxSkeletonTextToken =
    | 'body-sm'
    | 'body-md'
    | 'body-lg'
    | 'body-xl'
    | 'button-sm'
    | 'button-md'
    | 'button-xs'
    | 'display-xs'
    | 'display-sm'
    | 'display-md'
    | 'display-lg'
    | 'field-input'
    | 'field-label'
    | 'field-list-item'
    | 'heading-sm'
    | 'heading-md'
    | 'heading-lg'
    | 'modal-description'
    | 'modal-headline'
    | 'modal-title'
    | 'pill-md'
    | 'table-cell-sm'
    | 'table-cell-md'
    | 'table-column-header';

@Component({
    selector: 'qpx-skeleton-text',
    imports: [Skeleton],
    template: `
        @for (line of lineWidths(); track $index) {
            <qpx-skeleton
                [width]="line"
                [height]="lineBoxHeight"
                borderRadius="var(--qpx-radius-xs)"
                [context]="context()"
                [tone]="tone()"
                [effectiveHeight]="effectiveLineHeight()"
            />
        }
    `,
    styles: `
        :host {
            gap: var(--qpx-skeleton-text-gap, 0.5rem);
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'grid w-full',
        '[attr.aria-hidden]': 'true',
        '[style.font-size]': 'fontSize()',
        '[style.line-height]': 'lineHeight()',
    },
})
export class SkeletonText {
    //#region Inputs
    public readonly lines = input(1);
    public readonly width = input('100%');
    public readonly lastLineWidth = input('70%');
    public readonly text = input<QpxSkeletonTextToken>('body-md');
    public readonly effectiveLineHeight = input('80%');
    public readonly context = input<ContextFamily>();
    public readonly tone = input<ContextTone>();
    //#endregion

    //#region State
    protected readonly lineBoxHeight = '1lh';

    protected readonly fontSize = computed(() => `var(--text-${this.text()})`);

    protected readonly lineHeight = computed(
        () => `var(--text-${this.text()}--line-height)`
    );

    protected readonly lineWidths = computed(() => {
        const lineCount = Math.max(1, Math.floor(this.lines()));
        if (lineCount === 1) {
            return [this.width()];
        }

        return Array.from({ length: lineCount }, (_, index) =>
            index === lineCount - 1 ? this.lastLineWidth() : this.width()
        );
    });
    //#endregion
}
